import { useQuery } from "@tanstack/react-query";
import { HorarioSpa } from "./agendamientosSpaServicios";
import { TipoServicio } from "./ventasServicios";
import {
  apiDelete,
  generarAuthHeader,
  generarRuta,
  get,
  post,
  put,
} from "./divertidogsApiUtils";
import ServerError from "./ServerError";
import { MascotaResumida } from "./mascotasServicios";
import { Sector } from "./paisesServicios";
import { obtenerIntervalos } from "../utils/dateUtils";

interface INuevoItemCarrito {
  tipoServicio: TipoServicio;
}

type NuevoDatosTransporteTrayecto = {
  idTarifa: string;
  precioFinal: number;
};

type NuevoDatosTransporte = {
  fechas: string[];
  direccion: string;
  idSector: string;
  datosIda?: NuevoDatosTransporteTrayecto;
  datosVuelta?: NuevoDatosTransporteTrayecto;
  datosIdaVuelta?: NuevoDatosTransporteTrayecto;
};

export type NuevoItemCarritoGuarderia = INuevoItemCarrito & {
  tipoServicio: "GUARDERIA";
  datosReserva: {
    fechasProgramadas: string[];
    datosDiasIndividuales?: {
      precioFinal: number;
      idServicio: string;
      diasIndividuales: number;
    };
    datosPlan?: {
      idPlan: string;
      precioFinal: number;
    };
  };
  datosTransporte?: NuevoDatosTransporte;
};

const esNuevoItemGuarderia = (
  nuevoItem: NuevoItemCarrito
): nuevoItem is NuevoItemCarritoGuarderia => {
  return nuevoItem.tipoServicio === "GUARDERIA";
};

export type NuevoItemCarritoHotel = INuevoItemCarrito & {
  tipoServicio: "HOTEL";
  datosReserva: {
    fechasProgramadas: string[];
    datosTarifa?: {
      idTarifa: string;
      precioFinal: number;
    };
    datosNochesIndividuales?: {
      precioFinal: number;
      idServicio: string;
      nochesIndividuales: number;
    };
  };
  datosTransporte?: NuevoDatosTransporte;
};

const esNuevoItemHotel = (
  nuevoItem: NuevoItemCarrito
): nuevoItem is NuevoItemCarritoHotel => {
  return nuevoItem.tipoServicio === "HOTEL";
};

export type NuevoItemCarritoSpa = INuevoItemCarrito & {
  tipoServicio: "SPA";
  datosTarifa: {
    idTarifa: string;
    precioFinal: number;
  };
  fechasProgramadas: string[];
  horario: HorarioSpa;
};

const esNuevoItemSpa = (
  nuevoItem: NuevoItemCarrito
): nuevoItem is NuevoItemCarritoSpa => {
  return nuevoItem.tipoServicio === "SPA";
};

interface INuevoItemCarritoRequest {
  idPropietario: string;
  idMascota: string;
}

export type NuevoItemCarrito =
  | NuevoItemCarritoGuarderia
  | NuevoItemCarritoHotel
  | NuevoItemCarritoSpa;

export type NuevosItemsCarritoDeMascota = {
  itemsGuarderia: NuevoItemCarritoGuarderia[];
  itemsHotel: NuevoItemCarritoHotel[];
  itemsSpa: NuevoItemCarritoSpa[];
};

export const existeCarrito = async (): Promise<boolean> => {
  const url = generarRuta("carrito");
  const authHeader = generarAuthHeader();
  try {
    const res = await fetch(url, {
      method: "HEAD",
      headers: { Authorization: authHeader },
    });
    if (res.status === 404) {
      return Promise.resolve(false);
    }
    if (res.ok) {
      return Promise.resolve(true);
    }
    throw new ServerError(`Error buscando carrito: ${res.status} ${res.body}`);
  } catch (error: any) {
    const err = error as Error;
    throw new ServerError(err.message);
  }
};

export interface IItemCarrito {
  id: string;
  mascota: MascotaResumida;
  tipoServicio: TipoServicio;
  fechasProgramadas: string[];
}

export type ItemCarrito =
  | ItemCarritoGuarderia
  | ItemCarritoHotel
  | ItemCarritoSpa;

export const getNumeroFechas = (itemCarrito: IItemCarrito): number => {
  return itemCarrito.fechasProgramadas.length;
};

export type ServicioResumido = {
  id: string;
  nombre: string;
};

export type DatosTransporteTrayecto = {
  tarifa: ServicioResumido;
  precioFinal: number;
};

export type DatosTransporte = {
  fechas: string[];
  direccion: string;
  sector: Sector;
  datosIda?: DatosTransporteTrayecto;
  datosVuelta?: DatosTransporteTrayecto;
  datosIdaVuelta?: DatosTransporteTrayecto;
};

export interface ItemCarritoGuarderia extends IItemCarrito {
  datosPlan?: {
    plan: ServicioResumido;
    precioFinal: number;
  };
  datosDiasIndividuales?: {
    servicio: ServicioResumido;
    precioFinal: number;
    diasIndividuales: number;
  };
  datosTransporte?: DatosTransporte;
}

export const isItemCarritoGuarderia = (
  itemCarrito: IItemCarrito
): itemCarrito is ItemCarritoGuarderia => {
  return itemCarrito.tipoServicio === "GUARDERIA";
};

export interface ItemCarritoHotel extends IItemCarrito {
  datosTarifa?: {
    tarifa: ServicioResumido;
    precioFinal: number;
  };
  datosNochesIndividuales?: {
    tarifa: ServicioResumido;
    precioFinal: number;
    nochesIndividuales: number;
  };
  datosTransporte?: DatosTransporte;
}

export const isItemCarritoHotel = (
  itemCarrito: IItemCarrito
): itemCarrito is ItemCarritoHotel => {
  return itemCarrito.tipoServicio === "HOTEL";
};

export interface ItemCarritoSpa extends IItemCarrito {
  tarifa: ServicioResumido;
  precioFinal: number;
  horario: HorarioSpa;
}

export const isItemCarritoSpa = (
  itemCarrito: IItemCarrito
): itemCarrito is ItemCarritoSpa => {
  return itemCarrito.tipoServicio === "SPA";
};

export type NuevoItemCarritoGuarderiaRequest = INuevoItemCarritoRequest &
  NuevoItemCarritoGuarderia;

export type NuevoItemCarritoSpaRequest = INuevoItemCarritoRequest &
  NuevoItemCarritoSpa;

export type NuevoItemCarritoHotelRequest = INuevoItemCarritoRequest &
  NuevoItemCarritoHotel;

export type NuevoItemCarritoRequest =
  | NuevoItemCarritoGuarderiaRequest
  | NuevoItemCarritoSpaRequest
  | NuevoItemCarritoHotelRequest;

export type Carrito = {
  id: string;
  propietario?: {
    id: string;
    nombreCompleto: string;
    documento: string;
  };
  items: IItemCarrito[];
  descuento: number;
};

export const leerCarrito = async (): Promise<Carrito> => {
  const carrito = await get<Carrito>("carrito");
  if (!carrito) {
    throw new ServerError("Carrito para usuario no existe");
  }
  return carrito;
};

export const QUERY_LEER_CARRITO = "QUERY_LEER_CARRITO";

export const useLeerCarrito = (ejecutar: boolean) =>
  useQuery({
    queryKey: [QUERY_LEER_CARRITO],
    queryFn: async () => {
      return await leerCarrito();
    },
    enabled: ejecutar,
  });

export const QUERY_EXISTE_CARRITO = "QUERY_EXISTE_CARRITO";

export const useExisteCarrito = (enabled?: boolean) =>
  useQuery({
    queryKey: [QUERY_EXISTE_CARRITO],
    queryFn: async () => {
      return await existeCarrito();
    },
    enabled,
  });

export const agregarAlCarrito = async (
  item: NuevoItemCarritoRequest
): Promise<void> => {
  if (esNuevoItemGuarderia(item)) {
    const { tipoServicio: _, ...req } = item;
    await agregarGuarderiaAlCarrito(req);
    return;
  }
  if (esNuevoItemHotel(item)) {
    const { tipoServicio: _, ...req } = item;
    await agregarHotelAlCarrito(req);
    return;
  }
  if (esNuevoItemSpa(item)) {
    const { tipoServicio: _, ...req } = item;
    await agregarSpaAlCarrito(req);
    return;
  }
};

export const agregarGuarderiaAlCarrito = async (
  req: Omit<NuevoItemCarritoGuarderiaRequest, "tipoServicio">
): Promise<void> => {
  return post("carrito/items/guarderia", req);
};

export const agregarHotelAlCarrito = async (
  req: Omit<NuevoItemCarritoHotelRequest, "tipoServicio">
): Promise<void> => {
  return post("carrito/items/hotel", req);
};

export const agregarSpaAlCarrito = async (
  req: Omit<NuevoItemCarritoSpaRequest, "tipoServicio">
): Promise<void> => {
  return post("carrito/items/spa", req);
};

export type AgendarDeCarritoResponse = {
  id: string;
  idVenta: string;
};

export const calcularSubTotalTransporteGuar = (
  item: ItemCarritoGuarderia
): number => {
  if (!item.datosTransporte) {
    return 0;
  }
  const diasTrans = item.datosTransporte.fechas.length;

  const sumaPrecios =
    (item.datosTransporte.datosIda?.precioFinal || 0) +
    (item.datosTransporte.datosVuelta?.precioFinal || 0) +
    (item.datosTransporte.datosIdaVuelta?.precioFinal || 0);

  return sumaPrecios * diasTrans;
};

export const calcularSubTotalTransporteHot = (
  item: ItemCarritoHotel
): number => {
  if (!item.datosTransporte) {
    return 0;
  }
  const nochesTrans = item.datosTransporte.fechas;
  const intervalosNoches = obtenerIntervalos(nochesTrans);

  const sumaPrecios =
    (item.datosTransporte.datosIda?.precioFinal || 0) +
    (item.datosTransporte.datosVuelta?.precioFinal || 0) +
    (item.datosTransporte.datosIdaVuelta?.precioFinal || 0);

  return sumaPrecios * intervalosNoches.length;
};

export const calcularSubTotalGuarderia = (
  itemGuarderia: ItemCarritoGuarderia
): number => {
  const subPlan = (itemGuarderia.datosPlan?.precioFinal || 0) * 1;

  const subDiasIndividuales =
    (itemGuarderia.datosDiasIndividuales?.precioFinal || 0) *
    (itemGuarderia.datosDiasIndividuales?.diasIndividuales || 0);

  const subTotalTrans = calcularSubTotalTransporteGuar(itemGuarderia);

  return subPlan + subDiasIndividuales + subTotalTrans;
};

export const calcularSubTotalHotel = (itemHotel: ItemCarritoHotel): number => {
  const noches = getNumeroFechas(itemHotel);

  const nochesIndividuales =
    itemHotel.datosNochesIndividuales?.nochesIndividuales || 0;

  const nochesPlan = noches - nochesIndividuales;

  const subPlan = (itemHotel.datosTarifa?.precioFinal || 0) * nochesPlan;

  const subNochesIndividuales =
    (itemHotel.datosNochesIndividuales?.precioFinal || 0) * nochesIndividuales;

  const subTotalTrans = calcularSubTotalTransporteHot(itemHotel);

  return subPlan + subNochesIndividuales + subTotalTrans;
};

export const calcularSubTotalSpa = (itemSpa: ItemCarritoSpa): number => {
  const dias = getNumeroFechas(itemSpa);
  return itemSpa.precioFinal * dias;
};

const calcularSubTotalItem = (itemCarrito: ItemCarrito): number => {
  if (isItemCarritoGuarderia(itemCarrito)) {
    return calcularSubTotalGuarderia(itemCarrito);
  }
  if (isItemCarritoHotel(itemCarrito)) {
    return calcularSubTotalHotel(itemCarrito);
  }
  if (isItemCarritoSpa(itemCarrito)) {
    return calcularSubTotalSpa(itemCarrito);
  }
  return 0;
};

export const calcularTotalItems = (items: Array<ItemCarrito>): number => {
  return items
    .map((item) => calcularSubTotalItem(item))
    .reduce((prevSubTotal, currSubTotal) => {
      return prevSubTotal + currSubTotal;
    }, 0);
};

export type ExitoValidacionItemCarrito = {
  esValido: boolean;
};

export type ErrorValidacionItemCarrito = {
  esValido: boolean;
  mensaje: string;
};

export type ResultadoValidacionItemCarrito =
  | ExitoValidacionItemCarrito
  | ErrorValidacionItemCarrito;

export const isErrorValidacionItemCarrito = (
  res: ResultadoValidacionItemCarrito
): res is ErrorValidacionItemCarrito => {
  return !res.esValido;
};

export const isExitoValidacionItemCarrito = (
  res: ResultadoValidacionItemCarrito
): res is ExitoValidacionItemCarrito => {
  return res.esValido;
};

export const validarInclusionACarrito = (
  nuevoItem: NuevoItemCarritoRequest,
  carrito: Carrito
): ResultadoValidacionItemCarrito => {
  if (
    carrito.propietario &&
    nuevoItem.idPropietario !== carrito.propietario.id
  ) {
    return {
      esValido: false,
      mensaje:
        "El carrito existente contiene reservas de un propietario distinto.",
    };
  }

  if (esNuevoItemGuarderia(nuevoItem) || esNuevoItemHotel(nuevoItem)) {
    const fechasReservadasCarrito = carrito.items
      .filter(
        (item) =>
          item.mascota.id === nuevoItem.idMascota &&
          (isItemCarritoGuarderia(item) || isItemCarritoHotel(item))
      )
      .flatMap((item) => item.fechasProgramadas);

    if (
      fechasReservadasCarrito.some((fecha) =>
        nuevoItem.datosReserva.fechasProgramadas.includes(fecha)
      )
    ) {
      return {
        esValido: false,
        mensaje:
          "El carrito existente contiene estas fechas reservadas de hotel o guardería para esta mascota.",
      };
    }
  }

  if (esNuevoItemSpa(nuevoItem)) {
    const fechasReservadasCarrito = carrito.items
      .filter(
        (item) =>
          item.mascota.id === nuevoItem.idMascota && isItemCarritoSpa(item)
      )
      .flatMap((item) => item.fechasProgramadas);

    if (
      fechasReservadasCarrito.some((fecha) =>
        nuevoItem.fechasProgramadas.includes(fecha)
      )
    ) {
      return {
        esValido: false,
        mensaje:
          "El carrito existente contiene estas fechas reservadas de spa para esta mascota.",
      };
    }
  }

  return { esValido: true };
};

export type NuevoCarritoRequest = {
  idPropietario: string | null;
  descuento: number;
  itemsPorIdMascota: Map<string, NuevosItemsCarritoDeMascota>;
};

export const reemplazarCarrito = async (
  nuevoCarritoReq: NuevoCarritoRequest
): Promise<void> => {
  const req = {
    ...nuevoCarritoReq,
    itemsPorIdMascota: Object.fromEntries(nuevoCarritoReq.itemsPorIdMascota),
  };
  return await put("carrito", req);
};

const datosTransporteToDatosTransporteNuevoItem = (
  datosTransporte: DatosTransporte
): NuevoDatosTransporte => {
  const { datosIda, datosVuelta, datosIdaVuelta } = datosTransporte;
  return {
    fechas: datosTransporte.fechas,
    direccion: datosTransporte.direccion,
    idSector: datosTransporte.sector.id,
    datosIda: datosIda
      ? {
          idTarifa: datosIda!!.tarifa.id,
          precioFinal: datosIda!!.precioFinal,
        }
      : undefined,
    datosVuelta: datosVuelta
      ? {
          idTarifa: datosVuelta!!.tarifa.id,
          precioFinal: datosVuelta!!.precioFinal,
        }
      : undefined,
    datosIdaVuelta: datosIdaVuelta
      ? {
          idTarifa: datosIdaVuelta!!.tarifa.id,
          precioFinal: datosIdaVuelta!!.precioFinal,
        }
      : undefined,
  };
};

export const itemCarritoGuarderiaToNuevoItem = (
  itemGuarderia: ItemCarritoGuarderia
): NuevoItemCarritoGuarderia => {
  const {
    datosDiasIndividuales,
    datosPlan,
    datosTransporte,
    fechasProgramadas,
  } = itemGuarderia;
  const nuevosDatosDiasIndividuales = datosDiasIndividuales
    ? {
        precioFinal: datosDiasIndividuales!!.precioFinal,
        idServicio: datosDiasIndividuales!!.servicio.id,
        diasIndividuales: datosDiasIndividuales!!.diasIndividuales,
      }
    : undefined;

  const nuevosDatosPlan = datosPlan
    ? {
        precioFinal: datosPlan!!.precioFinal,
        idPlan: datosPlan!!.plan.id,
      }
    : undefined;

  const nuevosDatosTransporte = datosTransporte
    ? datosTransporteToDatosTransporteNuevoItem(datosTransporte!!)
    : undefined;

  return {
    tipoServicio: "GUARDERIA",
    datosReserva: {
      fechasProgramadas,
      datosDiasIndividuales: nuevosDatosDiasIndividuales,
      datosPlan: nuevosDatosPlan,
    },
    datosTransporte: nuevosDatosTransporte,
  };
};

export const itemCarritoHotelToNuevoItem = (
  itemHotel: ItemCarritoHotel
): NuevoItemCarritoHotel => {
  const {
    datosNochesIndividuales,
    datosTarifa,
    datosTransporte,
    fechasProgramadas,
  } = itemHotel;

  const nuevosDatosNochesIndividuales = datosNochesIndividuales
    ? {
        precioFinal: datosNochesIndividuales!!.precioFinal,
        idServicio: datosNochesIndividuales!!.tarifa.id,
        nochesIndividuales: datosNochesIndividuales!!.nochesIndividuales,
      }
    : undefined;

  const nuevosDatosTarifa = datosTarifa
    ? {
        precioFinal: datosTarifa!!.precioFinal,
        idTarifa: datosTarifa!!.tarifa.id,
      }
    : undefined;

  const nuevosDatosTransporte = datosTransporte
    ? datosTransporteToDatosTransporteNuevoItem(datosTransporte!!)
    : undefined;

  return {
    tipoServicio: "HOTEL",
    datosReserva: {
      fechasProgramadas,
      datosNochesIndividuales: nuevosDatosNochesIndividuales,
      datosTarifa: nuevosDatosTarifa,
    },
    datosTransporte: nuevosDatosTransporte,
  };
};

export const itemCarritoSpaToNuevoItem = (
  itemSpa: ItemCarritoSpa
): NuevoItemCarritoSpa => {
  return {
    tipoServicio: "SPA",
    datosTarifa: {
      idTarifa: itemSpa.tarifa.id,
      precioFinal: itemSpa.precioFinal,
    },
    fechasProgramadas: itemSpa.fechasProgramadas,
    horario: itemSpa.horario,
  };
};

export const eliminarItemGuarderia = async (id: string): Promise<void> => {
  return await apiDelete("carrito/items/guarderia/" + id);
};

export const eliminarItemHotel = async (id: string): Promise<void> => {
  return await apiDelete("carrito/items/hotel/" + id);
};

export const eliminarItemSpa = async (id: string): Promise<void> => {
  return await apiDelete("carrito/items/spa/" + id);
};
